/**
 * Generic error reporting utility
 *
 * This utility provides a centralized way to report errors (TypeScript errors, runtime errors, etc.)
 * to the backend API for tracking and monitoring purposes.
 */

import { getCookie } from "cookies-next";
import { toast } from "react-toastify";
import api from "@/lib/api";

/**
 * Extracts a readable string from any error value.
 * Avoids "[object Object]" by never stringifying plain objects directly.
 */
function getErrorMessage(error: unknown): string {
  if (error == null) return "Unknown error";
  if (typeof error === "string") return error;
  if (error instanceof Error) return error.message;
  if (typeof error === "object" && !Array.isArray(error)) {
    const o = error as Record<string, unknown>;
    if (typeof o.message === "string") return o.message;
    if (typeof o.error === "string") return o.error;
    if (o.details && typeof o.details === "object") {
      const d = o.details as Record<string, unknown>;
      const title = d.title != null ? String(d.title) : "";
      const detail = d.detail != null ? String(d.detail) : "";
      return title && detail
        ? `${title}: ${detail}`
        : detail || title || "Unknown error";
    }
    try {
      return JSON.stringify(error);
    } catch {
      return "Unknown error (unserializable object)";
    }
  }
  return String(error);
}

function isNoInternetConnectionMessage(message: string): boolean {
  return message.toLowerCase().includes("no internet connection");
}

export interface ErrorReportingOptions {
  /**
   * The error object (Error, TypeError, etc.)
   */
  error?: Error | unknown;

  /**
   * Custom error message (overrides error.message if provided)
   */
  message?: string;

  /**
   * Additional context about where/why the error occurred
   */
  context?: string;

  /**
   * The request path that caused the error (if applicable)
   */
  requestPath?: string;

  /**
   * Additional metadata to include in the error report
   */
  metadata?: Record<string, any>;

  /**
   * Whether to show a toast notification to the user (default: false)
   */
  showToast?: boolean;

  /**
   * Custom toast message (default: generic error message)
   */
  toastMessage?: string;

  /**
   * Whether to log the error to console (default: true)
   */
  logToConsole?: boolean;
}

/**
 * Reports an error to the backend API
 *
 * @param options - Error reporting options
 * @returns Promise that resolves when the error has been reported
 *
 * @example
 * // Report a TypeScript/runtime error
 * reportError({
 *   error: new TypeError('Cannot read property of undefined'),
 *   context: 'User profile component',
 *   showToast: true
 * });
 *
 * @example
 * // Report a custom error message
 * reportError({
 *   message: 'Failed to save form data',
 *   context: 'Form submission handler',
 *   requestPath: '/v1/form/save',
 *   metadata: { formId: 123 }
 * });
 *
 * @example
 * // Report error in try-catch block
 * try {
 *   // some code
 * } catch (error) {
 *   reportError({
 *     error,
 *     context: 'Data processing function',
 *     showToast: true
 *   });
 * }
 */
export async function reportError(
  options: ErrorReportingOptions = {},
): Promise<void> {
  console.log("🔴 reportError: Function called", {
    hasError: !!options.error,
    hasMessage: !!options.message,
    context: options.context,
  });

  // Early return if server-side
  if (typeof window === "undefined") {
    console.warn("reportError: Skipping - server-side");
    return;
  }

  const {
    error,
    message,
    context,
    requestPath,
    metadata = {},
    showToast = false,
    toastMessage,
    logToConsole = true,
  } = options;

  // Early return if server-side
  if (typeof window === "undefined") {
    console.warn("reportError: Skipping - server-side");
    return;
  }

  try {
    // Extract error message (never use raw error object — avoids "[object Object]")
    const errorMessage =
      message ||
      (error != null ? getErrorMessage(error) : null) ||
      "Unknown error occurred";

    if (isNoInternetConnectionMessage(errorMessage)) {
      if (logToConsole) {
        console.warn(
          "reportError: Skipping API calls — no internet connection",
          { message: errorMessage, context },
        );
      }
      if (showToast) {
        const messageToShow =
          toastMessage ||
          "We are very sorry for the inconvenience. The app encountered an error. We have informed the administrator. Please try again later.";
        toast.error(messageToShow, {
          toastId: `error-${Date.now()}`,
          autoClose: 5000,
        });
      }
      return;
    }

    // Get user information
    const token = getCookie("token");
    const caUserId = getCookie("ca_user_id");
    const userEmail = getCookie("email");
    const userFullName = getCookie("user_full_name");

    const errorApiEndpoint = "/v1/care_ascendium_error/new";

    // Get current page path
    const currentPage =
      typeof window !== "undefined" ? window.location.pathname : "unknown";

    // Build error description
    let errorDescription = errorMessage;

    if (context) {
      errorDescription = `${context}: ${errorMessage}`;
    }

    // Add error stack if available
    if (error instanceof Error && error.stack) {
      errorDescription += `\n\nStack Trace:\n${error.stack}`;
    }

    // Add metadata if provided
    if (Object.keys(metadata).length > 0) {
      try {
        const metadataStr = JSON.stringify(metadata);
        errorDescription += `\n\nAdditional Metadata:\n${metadataStr}`;
      } catch (e) {
        errorDescription += `\n\nAdditional Metadata: [Unable to stringify metadata]`;
      }
    }

    // Add timestamp
    errorDescription += `\n\nTimestamp: ${new Date().toISOString()}`;

    // Get user IP address (with timeout to not delay error reporting)
    let userIpAddress = "Unknown";
    try {
      // Try to get IP from a public service with 2 second timeout
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 2000);

      const ipResponse = await fetch("https://api.ipify.org?format=json", {
        signal: controller.signal,
      });
      clearTimeout(timeoutId);

      if (ipResponse.ok) {
        const ipData = await ipResponse.json();
        userIpAddress = ipData.ip || "Unknown";
      }
    } catch (ipError: unknown) {
      // If IP fetch fails or times out, continue without it
      if (!(ipError instanceof Error && ipError.name === "AbortError")) {
        console.warn("Failed to fetch IP address:", getErrorMessage(ipError));
      }
    }

    // Append IP address to error description
    errorDescription += `\n\nIP Address: ${userIpAddress}`;

    // Sanitize single quotes for database storage
    errorDescription = errorDescription.replace(/'/g, '"');

    // Prepare error payload
    const errorPayload = {
      p_request_path: requestPath || "N/A",
      p_current_page: currentPage,
      p_ca_app: "Web",
      p_error_desc: errorDescription,
      p_ca_user_id: caUserId || null,
      p_ca_user_email: userEmail || null,
      p_device_info:
        typeof navigator !== "undefined" ? navigator.userAgent : "N/A",
    };

    // Log to console if enabled
    if (logToConsole) {
      console.error("Error reported:", {
        message: errorMessage,
        context,
        requestPath,
        metadata,
        error:
          error instanceof Error
            ? {
                name: error.name,
                message: error.message,
                stack: error.stack,
              }
            : error,
      });
    }

    // Report error to API using the main api instance (same as existing working code)
    // The api interceptor checks requestPath != errorApiEndpoint to prevent infinite loops
    console.log("🔴 reportError: Making API call", {
      endpoint: errorApiEndpoint,
      hasToken: !!token,
    });

    try {
      if (token) {
        await api.post(errorApiEndpoint, errorPayload);
        const { createSupportTicketForAppError } =
          await import("@/components/SupportTicket/api");
        await createSupportTicketForAppError({
          subject: `[App error] ${requestPath || "client"} (reportError)`,
          question: `Automatic ticket from reportError().\n\n${errorDescription}`,
        });
      }
    } catch (apiError: unknown) {
      console.error("❌ reportError: API call FAILED", {
        message: getErrorMessage(apiError),
        status: (apiError as { response?: { status?: number } })?.response
          ?.status,
        data: (apiError as { response?: { data?: unknown } })?.response?.data,
      });
      // Don't throw - we don't want error reporting failures to break the app
    }

    // Show toast notification if requested
    if (showToast) {
      const messageToShow =
        toastMessage ||
        "We are very sorry for the inconvenience. The app encountered an error. We have informed the administrator. Please try again later.";

      toast.error(messageToShow, {
        toastId: `error-${Date.now()}`,
        autoClose: 5000,
      });
    }
  } catch (reportingError) {
    // If error reporting itself fails, log to console (extract message to avoid [object Object])
    console.error(
      "Failed to report error to API:",
      getErrorMessage(reportingError),
    );
    console.error(
      "Original error:",
      error != null ? getErrorMessage(error) : (message ?? "Unknown error"),
    );
  }
}

/**
 * Convenience function to report TypeScript/runtime errors
 *
 * @param error - The error object
 * @param context - Optional context about where the error occurred
 * @param showToast - Whether to show a toast notification (default: false)
 *
 * @example
 * try {
 *   // some code that might throw
 * } catch (error) {
 *   reportTypeScriptError(error, 'Component render', true);
 * }
 */
export function reportTypeScriptError(
  error: Error | unknown,
  context?: string,
  showToast: boolean = false,
): void {
  reportError({
    error,
    context,
    showToast,
  }).catch((reportingError) => {
    console.error(
      "Failed to report TypeScript error:",
      getErrorMessage(reportingError),
    );
  });
}

/**
 * Convenience function to report API errors
 *
 * @param error - The error object
 * @param requestPath - The API endpoint that caused the error
 * @param showToast - Whether to show a toast notification (default: true)
 *
 * @example
 * api.get('/v1/data')
 *   .catch((error) => {
 *     reportApiError(error, '/v1/data', true);
 *   });
 */
export function reportApiError(
  error: Error | unknown,
  requestPath?: string,
  showToast: boolean = true,
): void {
  reportError({
    error,
    requestPath,
    context: "API Request",
    showToast,
  }).catch((reportingError) => {
    console.error(
      "Failed to report API error:",
      getErrorMessage(reportingError),
    );
  });
}

/**
 * Convenience function to report custom errors with a message
 *
 * @param message - The error message
 * @param context - Optional context about where the error occurred
 * @param metadata - Optional additional metadata
 * @param showToast - Whether to show a toast notification (default: false)
 *
 * @example
 * if (!data) {
 *   reportCustomError(
 *     'Data validation failed',
 *     'Form submission',
 *     { formId: 123, field: 'email' },
 *     true
 *   );
 * }
 */
export function reportCustomError(
  message: string,
  context?: string,
  metadata?: Record<string, any>,
  showToast: boolean = false,
): void {
  reportError({
    message,
    context,
    metadata,
    showToast,
  }).catch((reportingError) => {
    console.error(
      "Failed to report custom error:",
      getErrorMessage(reportingError),
    );
  });
}

/**
 * Test function to verify error reporting is working
 * Call this from browser console: window.testErrorReporting()
 */
export function testErrorReporting(): void {
  console.log("🧪 Testing error reporting...");
  reportError({
    error: new Error("Test error from errorReporting utility"),
    context: "Error Reporting Test",
    metadata: { test: true, timestamp: new Date().toISOString() },
    showToast: false,
  })
    .then(() => {
      console.log("✅ Test error reporting completed successfully");
    })
    .catch((error) => {
      console.error("❌ Test error reporting failed:", getErrorMessage(error));
    });
}

// Make test function available globally for debugging
if (typeof window !== "undefined") {
  (window as any).testErrorReporting = testErrorReporting;
  console.log(
    "✅ testErrorReporting function available at window.testErrorReporting()",
  );

  // Also expose reportError directly for testing
  (window as any).reportErrorDirect = reportError;
  console.log(
    "✅ reportError function also available at window.reportErrorDirect()",
  );
}
